import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { TickLabel } from "Components/Merchant/FeeCalculator/CostsChart/TickLabel";
import { useResponsiveValue } from "Hooks/useResponsiveValue";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency, formatNumberOnly } from "Utils/CurrencyUtils";
import { useState, useMemo } from "react";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.MerchantFeeCalculator.CostsChart;

type DataBarID = "CurrentCosts" | "LegioneCosts" | "MooneCosts";
type DataBar = {
  id: DataBarID;
  costs: number;
  lightColor: string;
  darkColor: string;
};
type TickCallbackProps = {
  x: string | number;
  y: string | number;
  payload: { value: DataBarID };
};

type Props = {
  currentCosts: number;
  currentFee: string;
  legioneCosts: number;
  legioneFee: string;
  mooneCosts: number;
  mooneFee: string;
};

const tickFormatter = (value: number) =>
  formatNumberOnly(value, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });

export const CostsChart = ({
  currentCosts,
  currentFee,
  legioneCosts,
  legioneFee,
  mooneCosts,
  mooneFee,
}: Props) => {
  const [isHighContrast, setIsHighContrast] = useState(false);
  const getResponsiveValue = useResponsiveValue();
  const { t } = useResource();

  const idToTickFeeMap = useMemo(
    () => ({
      CurrentCosts: currentFee,
      LegioneCosts: legioneFee,
      MooneCosts: mooneFee,
    }),
    [currentFee, legioneFee, mooneFee],
  );

  const chartData: Array<DataBar> = useMemo(
    () => [
      {
        id: "CurrentCosts",
        costs: currentCosts,
        lightColor: "#006BFF",
        darkColor: "#004492",
      },
      {
        id: "LegioneCosts",
        costs: legioneCosts,
        lightColor: "#80B3FF",
        darkColor: "#0066CC",
      },
      {
        id: "MooneCosts",
        costs: mooneCosts,
        lightColor: "#C2DAFF",
        darkColor: "#0088FF",
      },
    ],
    [currentCosts, legioneCosts, mooneCosts],
  );

  const tick = getResponsiveValue([
    ({ x, y, payload }: TickCallbackProps) => (
      <TickLabel
        x={x}
        y={y}
        text={t(COMPONENT_RESOURCES.TickLabels[payload.value].Mobile)}
      />
    ),
    ({ x, y, payload }: TickCallbackProps) => (
      <TickLabel
        x={x}
        y={y}
        text={t(COMPONENT_RESOURCES.TickLabels[payload.value].Full)}
      />
    ),
    ({ x, y, payload }: TickCallbackProps) => (
      <TickLabel
        x={x}
        y={y}
        text={t(COMPONENT_RESOURCES.TickLabels[payload.value].Full)}
      />
    ),
    ({ x, y, payload }: TickCallbackProps) => (
      <TickLabel
        x={x}
        y={y}
        text={t(COMPONENT_RESOURCES.TickLabels[payload.value].Full)}
        description={t(
          COMPONENT_RESOURCES.TickLabels[payload.value].Description,
        )}
        fee={idToTickFeeMap[payload.value]}
      />
    ),
  ]);

  return (
    <Box sx={{ width: "100%", height: 400, mt: 4 }}>
      <Typography variant="h5" textAlign="center">
        {t(COMPONENT_RESOURCES.Title)}
      </Typography>
      <Box display="flex" justifyContent="end">
        <FormControlLabel
          label={t(COMPONENT_RESOURCES.HighContrast)}
          control={
            <Switch onChange={() => setIsHighContrast((prev) => !prev)} />
          }
        />
      </Box>
      <ResponsiveContainer
        width="100%"
        height="100%"
        initialDimension={{ width: 320, height: 200 }}
      >
        <BarChart
          data={chartData}
          margin={{
            top: 0,
            right: getResponsiveValue([0, 0, 0, 0, 30]),
            left: getResponsiveValue([15, 15, 15, 15, 30]),
            bottom: 50,
          }}
        >
          <XAxis dataKey="id" tick={tick} />
          <YAxis tickFormatter={tickFormatter} />
          <Tooltip
            formatter={(value) => [
              `${formatCurrency({ amount: Math.ceil(Number(value) || 0), currencyCode: "CZK" })}`,
              t(COMPONENT_RESOURCES.AnnualTransactionCosts),
            ]}
            labelFormatter={(id) => (
              <Typography component="span" textAlign="center">
                {t(COMPONENT_RESOURCES.TickLabels[id as DataBarID].Full)}
              </Typography>
            )}
          />
          <Bar dataKey="costs" isAnimationActive={false}>
            {chartData.map(({ id, lightColor, darkColor }) => (
              <Cell key={id} fill={isHighContrast ? darkColor : lightColor} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
